import { useContext, useMemo } from 'react';
import { Button } from 'reactstrap';
import { useParams } from 'react-router';
import styled from 'styled-components';

import { DISTANCE_UNITS } from '../../constants/units';
import { asQuantityWithUnit, asRoundedDecimal } from '../../utils/formatters';
import * as validators from '../../utils/validators';
import * as normalizers from '../../utils/normalizers';
import { PanelSection } from '../SidePanel';
import { LabeledInput, labelMinDisplayWidth } from './menu-components';
import { MapContext } from '../../contexts/MapContext';
import { layerIsCustom } from '../../utils/layers';
import { mapModes } from '../../constants/map';
import { MULTIPLE } from '../../constants/fields';

const LAT_LNG_STEP = 0.00001; // roughly equal to 1 meter at the equator

// Matches the offset of the other labels in this section.
const LayerSettingsLabel = styled.div`
  margin-left: ${labelMinDisplayWidth}px;
`;

const getLayerOrFeatureLabel = (
  layer,
  feature,
  selectedFeatures,
  allFeatures,
) => {
  if (selectedFeatures.length) {
    return asQuantityWithUnit(
      'selected features',
      'selected feature',
    )(selectedFeatures.length);
  }
  if (feature) {
    if (feature.properties.name) return feature.properties.name;

    const featureIndex = allFeatures.indexOf(feature);
    if (featureIndex !== -1) return `Feature ${featureIndex + 1}`;
    return 'Feature';
  }

  const baseName = `${layer.displayName ?? 'Layer'}`;
  if (layerIsCustom(layer)) return baseName;

  // For non-custom layers, we add 'default' to the end to remind users that they're editing the default value of width for the layer.
  return `${baseName} default`;
};

const LayerGeometrySection = ({ layerId, featureId, readonly }) => {
  const { farmId } = useParams();
  const {
    layers: {
      layers,
      setLayerWidth,
      setLayerFeatureProperty,
      setLayerFeatureCoordinates,
    },
    mapState: {
      activeLayerFeature,
      selectedLayerFeatureIds,
      selectedLayerFeatures,
      setMapMode,
    },
  } = useContext(MapContext);

  const layer = layers[layerId];
  const { data, width } = layer;

  const layerOrFeatureLabel = useMemo(
    () =>
      getLayerOrFeatureLabel(
        layer,
        activeLayerFeature,
        selectedLayerFeatures,
        data.features,
      ),
    [layer, activeLayerFeature, selectedLayerFeatures, data.features],
  );

  const isPointFeature = activeLayerFeature?.geometry?.type === 'Point';

  const updatePointCoordinates = (value, index) => {
    if (!activeLayerFeature) return;

    const coordinates = [...activeLayerFeature.geometry.coordinates];
    coordinates[index] = value;
    setLayerFeatureCoordinates(activeLayerFeature.id, farmId, coordinates);
  };

  const updatePointLng = (val) => updatePointCoordinates(val, 0);
  const updatePointLat = (val) => updatePointCoordinates(val, 1);
  const [pointLng, pointLat] = activeLayerFeature?.geometry?.coordinates ?? [];

  const onCoordinateInputFocus = () => {
    setMapMode(mapModes.LAYER_FEATURE_ENGAGED, activeLayerFeature.id);
  };

  const hasMultipleWidths =
    selectedLayerFeatures.length &&
    selectedLayerFeatures.some(
      (f) => f.properties?.width !== selectedLayerFeatures[0].properties?.width,
    );
  const defaultValue = hasMultipleWidths
    ? undefined
    : selectedLayerFeatures[0]?.properties?.width ??
      activeLayerFeature?.properties?.width ??
      width;
  const placeholder = hasMultipleWidths ? MULTIPLE : undefined;

  return (
    <PanelSection>
      <h4>Geometry — {layerOrFeatureLabel}</h4>

      {width != null && (
        <>
          <LabeledInput
            label="width"
            readonly={readonly}
            isValid={validators.isPositiveNumber}
            normalize={normalizers.asFloat}
            type="number"
            min={1}
            step="any"
            formatter={asRoundedDecimal}
            update={(value) => {
              if (selectedLayerFeatureIds.size || activeLayerFeature) {
                const activeFeatureIds = selectedLayerFeatureIds.size
                  ? selectedLayerFeatureIds
                  : [featureId];
                activeFeatureIds.forEach((activeFeatureId) => {
                  setLayerFeatureProperty(
                    activeFeatureId,
                    farmId,
                    // Clear width if it's the same as the layer width
                    'width',
                    value === width ? undefined : value,
                  );
                });
              } else {
                setLayerWidth(layerId, farmId, value);
              }
            }}
            defaultValue={defaultValue}
            placeholder={placeholder}
            unitSpec={DISTANCE_UNITS}
          />

          {activeLayerFeature && (
            <LayerSettingsLabel>
              {activeLayerFeature?.properties.width === undefined ? (
                <div className="text-muted font-tiny">Layer default</div>
              ) : (
                <Button
                  outline
                  size="sm"
                  className="font-tiny"
                  onClick={() => {
                    setLayerFeatureProperty(
                      featureId,
                      farmId,
                      // Clear width if it's the same as the layer width
                      'width',
                      undefined,
                    );
                  }}
                >
                  Reset to layer default
                </Button>
              )}
            </LayerSettingsLabel>
          )}
        </>
      )}

      {isPointFeature && (
        <>
          <LabeledInput
            label="lat"
            readonly={readonly}
            isValid={validators.isNumber}
            normalize={normalizers.asFloat}
            type="number"
            step={LAT_LNG_STEP}
            defaultValue={pointLat}
            update={updatePointLat}
            onFocus={onCoordinateInputFocus}
          />
          <LabeledInput
            label="lng"
            readonly={readonly}
            isValid={validators.isNumber}
            normalize={normalizers.asFloat}
            type="number"
            step={LAT_LNG_STEP}
            defaultValue={pointLng}
            update={updatePointLng}
            onFocus={onCoordinateInputFocus}
          />
        </>
      )}
    </PanelSection>
  );
};

export default LayerGeometrySection;
